import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PubNub from 'pubnub';
import { useDyteClient, DyteProvider } from '@dytesdk/react-web-core';
import { DyteMeeting } from '@dytesdk/react-ui-kit';
import styled, { keyframes, css } from 'styled-components';
import { FiArrowLeft, FiActivity, FiClock, FiPhoneOff } from 'react-icons/fi';
import {
  loadDoctorState,
  changeDoctorStatus,
  doStartCall,
  doCompleteCall,
  applyOpsUpdate,
} from '../../actions/bajajQueue';
import config from '../../config';
import {
  colors,
  space,
  radius,
  fontSize,
  fontWeight,
  lineHeight,
  fontFamily,
  shadow,
  focusRing,
  motion,
  z,
  withAlpha,
} from '../../styles/tokens';

// Local theme alias — pin tokens to short names for readable template strings.
const t = {
  bg:           colors.dark.bg,
  surface:      colors.dark.surface,
  surfaceHi:    colors.dark.surfaceHi,
  border:       colors.dark.border,
  borderStrong: colors.dark.borderStrong,
  text:         colors.dark.text,
  textMuted:    colors.dark.textMuted,
  textDim:      colors.dark.textDim,
  primary:      colors.primary,
  primaryHi:    colors.primaryHi,
  success:      colors.success,
  successHi:    colors.successHi,
  warning:      colors.warning,
  warningHi:    colors.warningHi,
  danger:       colors.danger,
  dangerHi:     colors.dangerHi,
  info:         colors.info,
};

const STATUS_META = {
  AVAILABLE: { color: t.success,   label: 'Available' },
  IN_CALL:   { color: t.warning,   label: 'In Call'   },
  WRAP_UP:   { color: t.info,      label: 'Wrap-Up'   },
  OFFLINE:   { color: t.textMuted, label: 'Offline'   },
};

// ────────────────────────────────────────────────────────────────────
// Animations
// ────────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
`;

const ripple = keyframes`
  0%   { box-shadow: 0 0 0 0 ${withAlpha(t.success, 0.45)}; }
  100% { box-shadow: 0 0 0 14px ${withAlpha(t.success, 0)}; }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40%           { opacity: 1; }
`;

// ────────────────────────────────────────────────────────────────────
// Reusable fragments
// ────────────────────────────────────────────────────────────────────

const focusRingMixin = (color = t.primary) => css`
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing(color, 0.4)};
  }
`;

const tabularNums = css`
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

// ────────────────────────────────────────────────────────────────────
// Page chrome
// ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  /* Subtle radial mesh — keeps the page from looking flat without
     resorting to loud gradients. Pinned to top-left so scrolled content
     keeps a consistent visual anchor. */
  background:
    radial-gradient(circle at 0% 0%, ${withAlpha(t.primary, 0.10)}, transparent 38%),
    radial-gradient(circle at 100% 12%, ${withAlpha(t.info, 0.07)}, transparent 32%),
    ${t.bg};
  color: ${t.text};
  font-family: ${fontFamily.sans};
  font-size: ${fontSize.base};
  line-height: ${lineHeight.normal};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 ${space[5]} ${space[7]};
`;

const TopNav = styled.nav`
  width: 100%;
  max-width: 56rem;
  display: flex;
  align-items: center;
  gap: ${space[3]};
  padding: ${space[4]} 0 ${space[3]};
  margin-bottom: ${space[4]};
  border-bottom: 1px solid ${t.border};
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  padding: ${space[1]} ${space[3]} ${space[1]} ${space[2]};
  background: transparent;
  border: 1px solid ${t.border};
  border-radius: ${radius.md};
  color: ${t.textMuted};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.medium};
  cursor: pointer;
  transition: color ${motion.fast}, border-color ${motion.fast}, background ${motion.fast};

  &:hover {
    color: ${t.text};
    border-color: ${t.borderStrong};
    background: ${t.surface};
  }
  &:active { background: ${t.surfaceHi}; }
  ${focusRingMixin(t.primary)}
`;

const NavTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const NavEyebrow = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: ${t.primary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const NavTitle = styled.div`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${t.text};
  letter-spacing: -0.005em;
`;

// ────────────────────────────────────────────────────────────────────
// Cards
// ────────────────────────────────────────────────────────────────────

const Card = styled.section`
  width: 100%;
  max-width: 56rem;
  position: relative;
  background: ${t.surface};
  border: 1px solid ${t.border};
  border-radius: ${radius.xl};
  padding: ${space[6]};
  box-shadow: ${shadow.lg};
  animation: ${fadeIn} ${motion.slow};

  & + & { margin-top: ${space[4]}; }

  /* Top-edge highlight — a 1px gradient hairline reads as a soft reflected light
     source. Keeps the card from looking flat without adding heavy shadow. */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: ${space[5]}; right: ${space[5]};
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${withAlpha(t.text, 0.08)} 50%,
      transparent
    );
    pointer-events: none;
  }
`;

const CardEyebrow = styled.h2`
  margin: 0 0 ${space[4]};
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: ${t.textDim};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

// ────────────────────────────────────────────────────────────────────
// Status row
// ────────────────────────────────────────────────────────────────────

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  flex-wrap: wrap;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[1]} ${space[3]} ${space[1]} ${space[2]};
  background: ${(p) => withAlpha(p.color, 0.1)};
  border: 1px solid ${(p) => withAlpha(p.color, 0.28)};
  border-radius: ${radius.pill};
  color: ${(p) => p.color};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  letter-spacing: 0.01em;
`;

const StatusDot = styled.span`
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
  background: ${(p) => p.color};
  animation: ${(p) => (p.pulse ? ripple : 'none')} 2.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  flex-shrink: 0;
`;

const DoctorName = styled.div`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.medium};
  color: ${t.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Segmented = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${space[1]};
  background: ${t.bg};
  border: 1px solid ${t.border};
  border-radius: ${radius.md};
  margin-left: auto;
`;

const SegmentBtn = styled.button`
  appearance: none;
  padding: ${space[1]} ${space[3]};
  background: ${(p) => (p.active ? t.surfaceHi : 'transparent')};
  border: 1px solid ${(p) => (p.active ? t.borderStrong : 'transparent')};
  border-radius: ${radius.sm};
  color: ${(p) => (p.active ? p.activeColor || t.text : t.textMuted)};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: color ${motion.fast}, background ${motion.fast}, border-color ${motion.fast};

  &:hover:not(:disabled) {
    color: ${t.text};
    background: ${t.surfaceHi};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  ${focusRingMixin(t.primary)}
`;

// ────────────────────────────────────────────────────────────────────
// Case card (the "next case" inside the main Card)
// ────────────────────────────────────────────────────────────────────

const CaseCard = styled.div`
  position: relative;
  padding: ${space[4]};
  margin-bottom: ${space[4]};
  background: ${t.bg};
  border: 1px solid ${t.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${withAlpha(t.primary, 0.6)} 50%,
      transparent
    );
  }
`;

const CaseTitle = styled.div`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  line-height: ${lineHeight.tight};
  letter-spacing: -0.01em;
  color: ${t.text};
  margin-bottom: ${space[1]};
`;

const CaseMeta = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${space[2]} ${space[5]};
  margin: ${space[3]} 0 0;
`;

const MetaCell = styled.div`
  min-width: 0;
`;

const MetaLabel = styled.dt`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.medium};
  color: ${t.textDim};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: ${space[1]};
`;

const MetaValue = styled.dd`
  margin: 0;
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${t.text};
  word-break: break-word;
  ${tabularNums}
`;

const WaitBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  margin-top: ${space[2]};
  padding: ${space[1]} ${space[2]};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${t.warning};
  background: ${withAlpha(t.warning, 0.12)};
  border: 1px solid ${withAlpha(t.warning, 0.25)};
  border-radius: ${radius.sm};
  ${tabularNums}
`;

// ────────────────────────────────────────────────────────────────────
// Buttons
// ────────────────────────────────────────────────────────────────────

const StartBtn = styled.button`
  width: 100%;
  padding: ${space[3]} ${space[4]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${space[2]};
  background: linear-gradient(180deg, ${t.successHi}, ${t.success});
  color: #04140a;
  border: 1px solid ${withAlpha('#000', 0.15)};
  border-radius: ${radius.md};
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  letter-spacing: -0.005em;
  cursor: pointer;
  box-shadow:
    0 1px 0 ${withAlpha('#fff', 0.18)} inset,
    ${shadow.sm};
  transition: transform ${motion.fast}, box-shadow ${motion.fast}, filter ${motion.fast};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 1px 0 ${withAlpha('#fff', 0.22)} inset,
      0 6px 16px -6px ${withAlpha(t.success, 0.55)};
  }
  &:active:not(:disabled) { transform: translateY(0); filter: brightness(0.96); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  ${focusRingMixin(t.success)}
`;

/* End-call uses a danger-ghost treatment — destructive-but-expected actions
   read as quieter than the primary CTA. Filled-on-hover for confirmation feel. */
const CompleteBtn = styled.button`
  width: 100%;
  margin-top: ${space[3]};
  padding: ${space[3]} ${space[4]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${space[2]};
  background: ${withAlpha(t.danger, 0.08)};
  color: ${t.dangerHi};
  border: 1px solid ${withAlpha(t.danger, 0.4)};
  border-radius: ${radius.md};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: background ${motion.fast}, color ${motion.fast}, border-color ${motion.fast};

  &:hover {
    background: ${t.danger};
    border-color: ${t.danger};
    color: #fff;
  }
  ${focusRingMixin(t.danger)}
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 0.3rem;

  span {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: currentColor;
    animation: ${dotPulse} 1.2s infinite ease-in-out both;
  }
  span:nth-child(2) { animation-delay: 0.16s; }
  span:nth-child(3) { animation-delay: 0.32s; }
`;

// ────────────────────────────────────────────────────────────────────
// Idle / empty state
// ────────────────────────────────────────────────────────────────────

const IdleCard = styled(Card)`
  text-align: center;
  padding: ${space[8]} ${space[5]} ${space[7]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space[2]};
`;

const IdleIconWrap = styled.div`
  width: 6.4rem;
  height: 6.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${radius.lg};
  background: ${withAlpha(t.primary, 0.1)};
  border: 1px solid ${withAlpha(t.primary, 0.22)};
  color: ${t.primary};
  margin-bottom: ${space[2]};
`;

const IdleTitle = styled.div`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  letter-spacing: -0.005em;
  color: ${t.text};
`;

const IdleText = styled.div`
  font-size: ${fontSize.base};
  color: ${t.textMuted};
  max-width: 36rem;
  line-height: ${lineHeight.normal};
`;

// ────────────────────────────────────────────────────────────────────
// Active call (Dyte container)
// ────────────────────────────────────────────────────────────────────

/* Width relaxed for the call surface — the Dyte UI wants room. The video
   wrapper sits flush inside a thinner card frame to maximize call area. */
const CallCard = styled(Card)`
  max-width: 96rem;
  padding: ${space[3]};
`;

const CallVideoWrap = styled.div`
  width: 100%;
  height: 70vh;
  border-radius: ${radius.lg};
  overflow: hidden;
  border: 1px solid ${t.border};
  background: #000;
  /* Bottom shadow on the video frame separates it from the Complete button
     without adding a visible divider. */
  box-shadow: 0 12px 24px -12px rgba(0, 0, 0, 0.6);
`;

// ────────────────────────────────────────────────────────────────────
// New-case toast (kept behind `false &&` flag in render — preserved)
// ────────────────────────────────────────────────────────────────────

const NewCaseToast = styled.div`
  position: fixed;
  top: ${space[5]};
  right: ${space[5]};
  min-width: 30rem;
  padding: ${space[3]} ${space[4]};
  background: ${t.surface};
  border: 1px solid ${withAlpha(t.primary, 0.5)};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.xl};
  animation: ${fadeIn} ${motion.base};
  z-index: ${z.toast};
`;

const ToastTitle = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  color: ${t.primaryHi};
  margin-bottom: ${space[2]};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ToastRow = styled.div`
  font-size: ${fontSize.sm};
  color: ${t.textMuted};
  margin-bottom: 0.3rem;
  ${tabularNums}

  span { color: ${t.text}; font-weight: ${fontWeight.semibold}; }
`;

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

const fmtSeconds = (s) => {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────

function BajajDoctorPortal(props) {
  const { doctorId, doctorFirstName, doctorLastName, doctorStatus, assignedCase, history } = props;

  const [inActiveCall, setInActiveCall] = useState(false);
  const [toast, setToast]               = useState(null);
  const [starting, setStarting]         = useState(false);
  const pubnubRef                       = useRef(null);
  const toastTimerRef                   = useRef(null);

  // Dyte hook — must be called at top level of functional component
  const [meeting, initMeeting] = useDyteClient();

  useEffect(() => {
    props.loadDoctorState();
    // Mount-once: dispatcher refs are unstable here (function-form
    // mapDispatchToProps), so we don't list them as deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!doctorId) return;
    setupPubNub(doctorId);
    return () => {
      if (pubnubRef.current) pubnubRef.current.unsubscribeAll();
    };
    // setupPubNub is defined inline below; only re-run on doctorId change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  const setupPubNub = (id) => {
    if (pubnubRef.current) pubnubRef.current.unsubscribeAll();
    const pn = new PubNub({
      publishKey:   config.pubnubPublishKey,
      subscribeKey: config.pubnubSubscribeKey,
      uuid: `doctor-portal-${id}`,
    });
    pn.addListener({
      message: ({ message }) => {
        if (message.name === 'new_case_inbound') {
          props.loadDoctorState();
          showToast({
            customerName:  message.customerName,
            proposalNumber: message.proposalNumber,
            waitSeconds:   message.waitSeconds,
          });
        }
        if (message.name === 'ops_queue_update') {
          props.applyOpsUpdate(message);
        }
      },
    });
    pn.subscribe({ channels: [`bajaj-instant-doctor-${id}`, 'bajaj-instant-ops'] });
    pubnubRef.current = pn;
  };

  const showToast = (data) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(data);
    toastTimerRef.current = setTimeout(() => setToast(null), 8000);
  };

  const handleStatusChange = (status) => {
    props.changeDoctorStatus(status);
  };

  const handleStartCall = async () => {
    if (!assignedCase || starting) return;
    setStarting(true);
    try {
      const result = await props.doStartCall(assignedCase.consultation_id);
      if (!result || result.status !== 'success') { setStarting(false); return; }
      await initMeeting({
        authToken: result.token,
        defaults: { audio: true, video: true },
      });
      setInActiveCall(true);
    } catch (err) {
      console.error('[handleStartCall]', err);
    }
    setStarting(false);
  };

  const handleCompleteCall = async () => {
    if (meeting) meeting.leaveRoom();
    await props.doCompleteCall(assignedCase.consultation_id);
    setInActiveCall(false);
  };

  const status     = doctorStatus ? doctorStatus.status : 'OFFLINE';
  const statusMeta = STATUS_META[status] || STATUS_META.OFFLINE;

  return (
    <Page>
      <TopNav>
        <BackBtn
          type="button"
          onClick={() => history && history.push('/home')}
          aria-label="Back to home"
        >
          <FiArrowLeft size={14} aria-hidden="true" />
          Back
        </BackBtn>
        <NavTitleGroup>
          <NavEyebrow>Bajaj · Instant</NavEyebrow>
          <NavTitle>Doctor Portal</NavTitle>
        </NavTitleGroup>
      </TopNav>

      {/* New-case toast — currently gated by `false &&` per existing logic. Preserved. */}
      {false && toast && (
        <NewCaseToast role="status" aria-live="polite">
          <ToastTitle>New Case Assigned</ToastTitle>
          <ToastRow>Customer: <span>{toast.customerName}</span></ToastRow>
          <ToastRow>App ID: <span>{toast.proposalNumber}</span></ToastRow>
          {toast.waitSeconds > 0 && (
            <ToastRow>Waited: <span>{fmtSeconds(toast.waitSeconds)}</span></ToastRow>
          )}
        </NewCaseToast>
      )}

      {/* Doctor status header */}
      <Card aria-labelledby="doctor-status-title">
        <CardEyebrow id="doctor-status-title">Doctor Status</CardEyebrow>
        <StatusRow aria-live="polite">
          <StatusPill color={statusMeta.color}>
            <StatusDot color={statusMeta.color} pulse={status === 'AVAILABLE'} />
            {statusMeta.label}
          </StatusPill>
          <DoctorName>Dr. {doctorFirstName} {doctorLastName}</DoctorName>
          <Segmented role="group" aria-label="Doctor availability">
            <SegmentBtn
              type="button"
              active={status === 'AVAILABLE'}
              activeColor={t.success}
              aria-pressed={status === 'AVAILABLE'}
              onClick={() => handleStatusChange('AVAILABLE')}
              disabled={status === 'IN_CALL'}
            >
              Go Available
            </SegmentBtn>
            <SegmentBtn
              type="button"
              active={status === 'OFFLINE'}
              activeColor={t.text}
              aria-pressed={status === 'OFFLINE'}
              onClick={() => handleStatusChange('OFFLINE')}
              disabled={status === 'IN_CALL'}
            >
              Go Offline
            </SegmentBtn>
          </Segmented>
        </StatusRow>
      </Card>

      {/* Active Dyte call */}
      {inActiveCall && meeting ? (
        <CallCard aria-label="Active video call">
          <CallVideoWrap>
            <DyteProvider value={meeting}>
              <DyteMeeting mode="fill" meeting={meeting} />
            </DyteProvider>
          </CallVideoWrap>
          <CompleteBtn type="button" onClick={handleCompleteCall}>
            <FiPhoneOff size={16} aria-hidden="true" />
            End Call &amp; Mark Complete
          </CompleteBtn>
        </CallCard>
      ) : assignedCase ? (
        <Card aria-labelledby="next-case-title">
          <CardEyebrow id="next-case-title">Next Case</CardEyebrow>
          <CaseCard>
            <CaseTitle>{assignedCase.name}</CaseTitle>
            {assignedCase.waitSeconds > 0 && (
              <WaitBadge>
                <FiClock size={12} aria-hidden="true" />
                Waited {fmtSeconds(assignedCase.waitSeconds)}
              </WaitBadge>
            )}
            <CaseMeta>
              <MetaCell>
                <MetaLabel>Proposal Number</MetaLabel>
                <MetaValue>{assignedCase.proposalNumber}</MetaValue>
              </MetaCell>
              <MetaCell>
                <MetaLabel>Gender</MetaLabel>
                <MetaValue>{assignedCase.gender || '—'}</MetaValue>
              </MetaCell>
              <MetaCell>
                <MetaLabel>Phone</MetaLabel>
                <MetaValue>{assignedCase.phone}</MetaValue>
              </MetaCell>
              <MetaCell>
                <MetaLabel>Status</MetaLabel>
                <MetaValue>{assignedCase.status}</MetaValue>
              </MetaCell>
            </CaseMeta>
          </CaseCard>
          <StartBtn type="button" onClick={handleStartCall} disabled={starting}>
            {starting ? (
              <>
                Connecting
                <Dots aria-hidden="true"><span /><span /><span /></Dots>
              </>
            ) : 'Start Call Now'}
          </StartBtn>
        </Card>
      ) : (
        <IdleCard aria-labelledby="idle-title">
          <IdleIconWrap>
            <FiActivity size={28} aria-hidden="true" />
          </IdleIconWrap>
          {status === 'OFFLINE' ? (
            <>
              <IdleTitle id="idle-title">You're offline</IdleTitle>
              <IdleText>Click <strong>Go Available</strong> above to start receiving cases.</IdleText>
            </>
          ) : status === 'WRAP_UP' ? (
            <>
              <IdleTitle id="idle-title">Wrap-up in progress</IdleTitle>
              <IdleText>You'll be auto-set to Available shortly.</IdleText>
            </>
          ) : (
            <>
              <IdleTitle id="idle-title">You're available</IdleTitle>
              <IdleText>Waiting for the next case — new cases will appear here automatically.</IdleText>
            </>
          )}
        </IdleCard>
      )}
    </Page>
  );
}

const mapStateToProps = (state) => ({
  doctorId:        state.user.doctorId,
  doctorFirstName: state.user.doctorFirstName,
  doctorLastName:  state.user.doctorLastName,
  doctorStatus:    state.bajajQueue.doctorStatus,
  assignedCase:    state.bajajQueue.assignedCase,
});

const mapDispatchToProps = {
  loadDoctorState,
  changeDoctorStatus,
  doStartCall,
  doCompleteCall,
  applyOpsUpdate,
};

export default connect(mapStateToProps, mapDispatchToProps)(BajajDoctorPortal);
